import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Button } from '../components/ui/button';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    pickup_location: '',
    delivery_location: '',
    service_type: '',
    load_details: '',
    preferred_date: '',
  });
  const [date, setDate] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service_type: value }));
  };

  const handleDateSelect = (selectedDate) => {
    setDate(selectedDate);
    if (selectedDate) {
      setFormData((prev) => ({ 
        ...prev, 
        preferred_date: format(selectedDate, 'yyyy-MM-dd') 
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/quotes`, { ...formData, sms_consent: smsConsent });
      setSubmitStatus({ type: 'success', message: response.data.message });
      setFormData({
        name: '',
        phone: '',
        email: '',
        pickup_location: '',
        delivery_location: '',
        service_type: '',
        load_details: '',
        preferred_date: '',
      });
      setDate(null);
      setSmsConsent(false);
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Something went wrong. Please try again or contact us directly.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      value: '224-374-7400',
      link: 'tel:224-374-7400',
      color: 'red',
    },
    {
      icon: Mail,
      title: 'Email',
      value: 'contact@topfleetlogistics.com',
      link: 'mailto:contact@topfleetlogistics.com',
      color: 'blue',
    },
    {
      icon: MapPin,
      title: 'Location',
      value: 'Augusta, Georgia',
      link: null,
      color: 'red',
    },
    {
      icon: Clock,
      title: 'Hours',
      value: 'Mon-Sat: 7AM - 8PM',
      link: null,
      color: 'blue',
    },
  ];

  return (
    <div className="bg-texture min-h-screen" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-brand-surface border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
              GET A <span className="text-brand-red">QUOTE</span>
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-2">
              Ready to move? Fill out the form below and we'll build your custom quote.
            </p>
            <p className="text-brand-red/80 text-sm font-medium">We respond within 1 hour</p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 md:py-32" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Contact Info */}
            <motion.div
              className="lg:col-span-1"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl text-white mb-8">
                CONTACT <span className="text-brand-blue">INFO</span>
              </h2>
              
              <div className="space-y-6">
                {contactInfo.map((info) => (
                  <div 
                    key={info.title}
                    className="flex items-start gap-4"
                    data-testid={`contact-info-${info.title.toLowerCase()}`}
                  >
                    <div className={`icon-container ${info.color} flex-shrink-0`} style={{ width: '48px', height: '48px' }}>
                      <info.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-slate-500 text-sm mb-1">{info.title}</p>
                      {info.link ? (
                        <a 
                          href={info.link}
                          className="text-white font-semibold hover:text-brand-red transition-colors"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-white font-semibold">{info.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-12 p-6 rounded-xl bg-brand-surface border border-white/10">
                <h3 className="font-heading text-lg text-white mb-4">SERVICE AREAS</h3>
                <p className="text-slate-400 text-sm mb-4">
                  Based in Augusta, GA — Serving Local, Regional, and Interstate Routes
                </p>
                <p className="text-slate-500 text-sm">
                  Covering Georgia, South Carolina, and surrounding regions with nationwide transport capability.
                </p>
              </div>

              {/* Service Guarantee */}
              <div className="mt-6 p-6 rounded-xl bg-brand-red/5 border border-brand-red/15" data-testid="contact-guarantee">
                <h3 className="font-heading text-sm text-brand-red mb-3 tracking-wide">TOPFLEET SERVICE GUARANTEE</h3>
                <ul className="space-y-2.5">
                  <li className="flex items-start gap-2 text-slate-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-brand-red flex-shrink-0 mt-0.5" />
                    On-time delivery or you receive a service discount
                  </li>
                  <li className="flex items-start gap-2 text-slate-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-brand-red flex-shrink-0 mt-0.5" />
                    Full damage protection on every transport
                  </li>
                  <li className="flex items-start gap-2 text-slate-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-brand-red flex-shrink-0 mt-0.5" />
                    Professional service backed by our satisfaction commitment
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Quote Form */}
            <motion.div
              className="lg:col-span-2"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-brand-surface rounded-xl border border-white/10 p-8 md:p-12">
                <h2 className="font-heading text-2xl text-white mb-4">
                  REQUEST YOUR <span className="text-brand-red">QUOTE</span>
                </h2>

                {/* Direct quote-team contact line (Google Workspace) */}
                <div
                  className="mb-8 rounded-lg border border-brand-red/20 bg-brand-red/5 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3"
                  data-testid="quote-direct-contact"
                >
                  <div className="flex-shrink-0">
                    <Mail className="w-5 h-5 text-brand-red" />
                  </div>
                  <div className="text-sm text-slate-300 leading-relaxed">
                    <span className="font-semibold text-white">Need assistance with a custom shipment or logistics request?</span>{' '}
                    Email us directly at{' '}
                    <a
                      href="mailto:quotes@topfleetlogistics.com"
                      className="font-semibold text-brand-red hover:underline"
                      data-testid="quote-direct-email"
                    >
                      quotes@topfleetlogistics.com
                    </a>
                  </div>
                </div>

                {submitStatus?.type === 'success' ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                    data-testid="quote-success-message"
                  >
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
                    <h3 className="font-heading text-2xl text-white mb-4">Quote Request Received!</h3>
                    <p className="text-slate-400">{submitStatus.message}</p>
                    <button
                      onClick={() => setSubmitStatus(null)}
                      className="btn-secondary mt-8"
                    >
                      Submit Another Request
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Name */}
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-slate-300">Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Your full name"
                          required
                          className="input-dark bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-brand-red focus:ring-brand-red"
                          data-testid="quote-name-input"
                        />
                      </div>

                      {/* Phone */}
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-slate-300">Phone *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="(123) 456-7890"
                          required
                          className="input-dark bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-brand-red focus:ring-brand-red"
                          data-testid="quote-phone-input"
                        />
                      </div>

                      {/* Email */}
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-300">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="you@example.com"
                          required
                          className="input-dark bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-brand-red focus:ring-brand-red"
                          data-testid="quote-email-input"
                        />
                      </div>

                      {/* Service Type */}
                      <div className="space-y-2">
                        <Label htmlFor="service_type" className="text-slate-300">Type of Service *</Label>
                        <Select onValueChange={handleServiceChange} value={formData.service_type} required>
                          <SelectTrigger 
                            className="bg-white/5 border-white/10 text-white focus:border-brand-red focus:ring-brand-red"
                            data-testid="quote-service-select"
                          >
                            <SelectValue placeholder="Select service type" />
                          </SelectTrigger>
                          <SelectContent className="bg-brand-surface border-white/10">
                            <SelectItem value="freight" className="text-white hover:bg-white/10">Commercial Freight</SelectItem>
                            <SelectItem value="moving" className="text-white hover:bg-white/10">Residential Moving</SelectItem>
                            <SelectItem value="delivery" className="text-white hover:bg-white/10">On-Demand Delivery</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Pickup Location */}
                      <div className="space-y-2">
                        <Label htmlFor="pickup_location" className="text-slate-300">Pickup Location *</Label>
                        <Input
                          id="pickup_location"
                          name="pickup_location"
                          value={formData.pickup_location}
                          onChange={handleInputChange}
                          placeholder="City, State or Address"
                          required
                          className="input-dark bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-brand-red focus:ring-brand-red"
                          data-testid="quote-pickup-input"
                        />
                      </div>

                      {/* Delivery Location */}
                      <div className="space-y-2">
                        <Label htmlFor="delivery_location" className="text-slate-300">Delivery Location *</Label>
                        <Input
                          id="delivery_location"
                          name="delivery_location"
                          value={formData.delivery_location}
                          onChange={handleInputChange}
                          placeholder="City, State or Address"
                          required
                          className="input-dark bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-brand-red focus:ring-brand-red"
                          data-testid="quote-delivery-input"
                        />
                      </div>
                    </div>

                    {/* Preferred Date */}
                    <div className="space-y-2">
                      <Label className="text-slate-300">Preferred Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white"
                            data-testid="quote-date-picker"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {date ? format(date, 'PPP') : <span className="text-slate-500">Pick a date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-brand-surface border-white/10" align="start">
                          <Calendar
                            mode="single"
                            selected={date}
                            onSelect={handleDateSelect}
                            initialFocus
                            className="bg-brand-surface"
                            disabled={(date) => date < new Date()}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Load Details */}
                    <div className="space-y-2">
                      <Label htmlFor="load_details" className="text-slate-300">Load Details</Label>
                      <Textarea
                        id="load_details"
                        name="load_details"
                        value={formData.load_details}
                        onChange={handleInputChange}
                        placeholder="Describe your load (size, weight, special handling requirements, etc.)"
                        rows={4}
                        className="input-dark bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-brand-red focus:ring-brand-red resize-none"
                        data-testid="quote-details-textarea"
                      />
                    </div>

                    {submitStatus?.type === 'error' && (
                      <p className="text-red-400 text-sm" data-testid="quote-error-message">
                        {submitStatus.message}
                      </p>
                    )}

                    {/* Consent Checkbox (Legal & Policies) */}
                    <label className="flex items-start gap-3 cursor-pointer group" data-testid="quote-consent-label">
                      <input
                        type="checkbox"
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                        className="mt-1 w-4 h-4 rounded border-white/20 bg-white/5 text-brand-red focus:ring-brand-red/50 cursor-pointer"
                        data-testid="quote-consent-checkbox"
                        required
                      />
                      <span className="text-slate-400 text-sm leading-relaxed">
                        I agree to the{' '}
                        <Link to="/legal" className="text-brand-red hover:underline" target="_blank">Legal & Policies</Link>
                      </span>
                    </label>

                    {/* SMS Consent — A2P 10DLC compliant, optional, unchecked by default, NOT bundled with TOS */}
                    <div className="pt-2 mt-2 border-t border-white/10" data-testid="quote-sms-consent-section">
                      <label className="flex items-start gap-3 cursor-pointer group" data-testid="quote-sms-consent-label">
                        <input
                          type="checkbox"
                          checked={smsConsent}
                          onChange={(e) => setSmsConsent(e.target.checked)}
                          className="mt-1 w-4 h-4 rounded border-white/20 bg-white/5 text-brand-red focus:ring-brand-red/50 cursor-pointer"
                          data-testid="quote-sms-consent-checkbox"
                        />
                        <span className="text-slate-400 text-sm leading-relaxed">
                          I agree to receive SMS communications from TopFleet Logistics related to my quote, booking, and delivery updates. Message frequency may vary. Message and data rates may apply. Reply STOP to unsubscribe.
                        </span>
                      </label>
                      <p className="text-slate-500 text-xs mt-2 ml-7" data-testid="quote-sms-consent-note">
                        (Optional &ndash; you can submit this form without agreeing to SMS messages)
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-cta w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="quote-submit-button"
                    >
                      {isSubmitting ? (
                        'Submitting...'
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Get My Quote
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
